import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Save, X, FileText, User, MapPin,
  Calendar, Hash, DollarSign, Percent, Calculator,
  Building, Phone, Mail, Search, ChevronDown, CheckCircle,
  HelpCircle
} from 'lucide-react';
import Breadcrumb from '../../components/Common/Breadcrumb';
import HelpModal from '../../components/HelpModal/HelpModal';
import CustomerSelectionModal from '../../components/CustomerSelectionModal/CustomerSelectionModal';
import ServiceSelectionTable from '../../components/ServiceSelectionTable/ServiceSelectionTable';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { toast } from 'react-toastify';
import autoIncrementService from '../../services/autoIncrementService';
import companyService from '../../services/companyService';
import taxInvoiceService from '../../services/taxInvoiceService';
import paymentTermsMasterService from '../../services/paymentTermsMasterService';
import projectService from '../../services/projectService';
import { calculateInvoiceGST } from '../../utils/gstCalculator';
import { formatCurrency } from '../../utils/currencyFormat';
import '../Accounts/PaymentForm.css';

const TaxInvoiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = id && id !== 'new';
  const isView = window.location.pathname.includes('/view/');
  const isApprove = window.location.pathname.includes('/approve/');

  const [activeTab, setActiveTab] = useState('invoiceDetails');
  const [showHelp, setShowHelp] = useState(false);
  const [formData, setFormData] = useState({
    // Invoice Details
    invoiceNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    poNumber: '',
    referenceNumber: '',
    
    // Customer Details
    customerId: '',
    customerName: '',
    customerCode: '', // Keep for backend but hidden from UI
    customerCategory: '', // Will be populated when customer is selected
    customerAddress: '',
    customerCity: '',
    customerState: '',
    customerPincode: '',
    customerPhone: '',
    customerEmail: '',
    customerGSTIN: '',
    customerPaymentTerms: '',
    currency: '', // Will be populated when customer is selected
    
    // Billing Details
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingPincode: '',
    
    // Item Details
    items: [
      {
        id: 1,
        itemCode: '',
        itemName: '',
        description: '',
        quantity: 1,
        unit: 'Nos',
        rate: 0,
        amount: 0,
        discount: 0,
        taxableAmount: 0
      }
    ],
    
    // GST Details
    cgstRate: 9,
    sgstRate: 9,
    igstRate: 18,
    cgstAmount: 0,
    sgstAmount: 0,
    igstAmount: 0,
    totalTaxAmount: 0,
    totalAmountBeforeRoundOff: 0,
    roundOffAmount: 0,
    subtotal: 0,
    totalAmount: 0,
    gstType: 'CGST_SGST',
    
    // Place of supply (customer billing state — used when customer has no GSTIN)
    placeOfSupply: '',
    notes: '',
    terms: '',
    status: 'Draft',
    remark: '',
    totalValue: 0.00,
    
    // Project Details
    projectName: '',
    projectId: '',
    referenceModel: 'Customer' // Default to 'Customer'
  });

  const [loading, setLoading] = useState(false);
  const [isLoadingInvoiceNumber, setIsLoadingInvoiceNumber] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [serviceData, setServiceData] = useState([]);
  const [showServiceTable, setShowServiceTable] = useState(false); // Show only when customer is selected
  const [companyGSTIN, setCompanyGSTIN] = useState('');
  const [companyState, setCompanyState] = useState('');
  const [customerGSTIN, setCustomerGSTIN] = useState('');
  const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const serviceTableRef = useRef(null);
  const [canCreateInvoice, setCanCreateInvoice] = useState(false);

  // Load company GSTIN and payment terms on component mount
  useEffect(() => {
    const fetchCompanyGSTIN = async () => {
      try {
        const response = await companyService.getCompanyDetails();
        if (response && response.success && response.data) {
          if (response.data.GSTIN) {
            setCompanyGSTIN(response.data.GSTIN);
          }
          const billingState = response.data.companyBillingAddress?.state || '';
          if (billingState) {
            setCompanyState(billingState);
          }
        }
      } catch (error) {
        console.error('Error fetching company GSTIN:', error);
        
        // Check if it's an authentication error
        if (error.response?.status === 401) {
          toast.error('Please log in to continue');
          navigate('/login');
          return;
        }
        
        // Fallback: try to get from localStorage or set a default
        const storedGSTIN = localStorage.getItem('companyGSTIN');
        if (storedGSTIN) {
          setCompanyGSTIN(storedGSTIN);
        } else {
          // Set a default GSTIN for testing
          setCompanyGSTIN('27AAGCI0729M1ZB'); // Default GSTIN for testing
        }
      }
    };

    const fetchPaymentTerms = async () => {
      try {
        const response = await paymentTermsMasterService.getActivePaymentTerms();
        if (response && response.success) {
          setPaymentTermsOptions(response.data || []);
        } else {
          // Only log, don't show toast - API interceptor will handle it
          console.warn('Payment terms response was not successful:', response?.message);
        }
      } catch (error) {
        // Only log, don't show toast - API interceptor already handles network/API errors
        console.error('Error fetching payment terms:', error);
        // Don't show duplicate toast - the API interceptor will show the error
      }
    };

    fetchCompanyGSTIN();
    fetchPaymentTerms();
  }, [navigate]);

  // Load data for edit/view/approve mode
  useEffect(() => {
    if (isEdit || isView || isApprove) {
      loadInvoiceData();
    } else {
      // Only generate invoice number for new invoices (create mode)
      generateInvoiceNumber();
    }
  }, [id, isEdit, isView, isApprove]);

  // Trigger GST calculation when switching to GST Details tab or when GSTINs change
  useEffect(() => {
    if (activeTab === 'gstDetails' && formData.totalValue > 0) {
      calculateGST(formData.totalValue);
    }
  }, [activeTab, formData.totalValue, companyGSTIN, customerGSTIN, formData.placeOfSupply, formData.customerCategory, companyState]);

  const loadInvoiceData = async () => {
    setLoading(true);
    try {
      if (id && id !== 'new') {
        // Load actual invoice data from API
        const response = await taxInvoiceService.getTaxInvoiceById(id);
        if (response && response.success && response.data) {
          const invoice = response.data;
          
          // Format date fields
          const invoiceDate = invoice.serviceInvoiceDate 
            ? new Date(invoice.serviceInvoiceDate).toISOString().split('T')[0] 
            : new Date().toISOString().split('T')[0];
          const dueDate = invoice.dueDate 
            ? new Date(invoice.dueDate).toISOString().split('T')[0] 
            : '';
          const poDate = invoice.PODate 
            ? new Date(invoice.PODate).toISOString().split('T')[0] 
            : '';
          
          // Get currency from invoice or customer (leave blank if not available)
          const currency = invoice.currency || 
                          (invoice.customer && invoice.customer.customerCurrency) || 
                          '';
          
          // Extract GST rates from serviceDetails or GSTDetails
          // Get rates from first service that has them, or from GSTDetails
          let cgstRate = 9; // Default
          let sgstRate = 9; // Default
          let igstRate = 18; // Default
          
          if (invoice.serviceDetails && invoice.serviceDetails.length > 0) {
            // Get rates from first service
            const firstService = invoice.serviceDetails[0];
            cgstRate = firstService.cgstRate || 9;
            sgstRate = firstService.sgstRate || 9;
            igstRate = firstService.igstRate || 18;
          } else if (invoice.GSTDetails && invoice.GSTDetails.length > 0) {
            // Fallback to GSTDetails if serviceDetails not available
            const firstGSTDetail = invoice.GSTDetails[0];
            cgstRate = firstGSTDetail.cgstRate || 9;
            sgstRate = firstGSTDetail.sgstRate || 9;
            igstRate = firstGSTDetail.igstRate || 18;
          }
          
          setFormData({
            invoiceNumber: invoice.serviceInvoiceNumber || '',
            invoiceDate: invoiceDate,
            dueDate: poDate, // PO Date is stored in dueDate field
            poNumber: invoice.PONo || '',
            referenceNumber: invoice.referenceModel || '',
            customerId: invoice.customer?._id || invoice.customer || '',
            customerName: invoice.customerName || '',
            customerCode: invoice.customerCode || (invoice.customer?.customerCode || ''),
            customerCategory: invoice.customerCategory || 'Domestic', // Load customer category from invoice
            customerGSTIN: invoice.customerGSTIN || (invoice.customer?.GSTIN || ''),
            customerPaymentTerms: invoice.customerPaymentTerms || '',
            currency: currency,
            projectName: invoice.projectName || '',
            projectId: invoice.project?._id || invoice.project || '',
            referenceModel: invoice.referenceModel || 'Customer',
            items: invoice.serviceDetails?.map((service, index) => ({
              id: service._id || service.service?._id || index + 1,
              serviceId: service.service?._id || service.service || '',
              itemCode: service.serviceCode || '',
              itemName: service.serviceDescription || '',
              serviceDescription: service.serviceDescription || '',
              description: service.serviceDescription || '',
              quantity: service.qty || 0,
              unit: 'Nos',
              rate: service.unitRate || 0,
              amount: service.lineValue || 0,
              discount: 0,
              taxableAmount: service.lineValue || 0,
              lineValue: service.lineValue || 0,
              // Include GST rates from service
              cgstRate: service.cgstRate || cgstRate,
              sgstRate: service.sgstRate || sgstRate,
              igstRate: service.igstRate || igstRate,
              SACCode: service.SACCode || '',
              selectSAC: service.SACCode || '',
              additionalInfo: service.additionalInfo || ''
            })) || [],
            totalValue: invoice.totalValue || 0,
            cgstAmount: invoice.totalCGSTAmount || 0,
            sgstAmount: invoice.totalSGSTAmount || 0,
            igstAmount: invoice.totalIGSTAmount || 0,
            totalTaxAmount: invoice.totalTaxAmount || 0,
            totalAmount: invoice.totalAmountWithTax || 0,
            subtotal: invoice.totalValue || 0,
            cgstRate: cgstRate, // Load GST rates
            sgstRate: sgstRate,
            igstRate: igstRate,
            placeOfSupply: invoice.placeOfSupply || '',
            billingState: invoice.placeOfSupply || '',
            customerState: invoice.placeOfSupply || '',
            gstType: invoice.gstType || 'CGST_SGST',
            notes: invoice.remarks || '',
            terms: invoice.paymentTerms || '',
            status: invoice.status || 'Draft'
          });
          
          // Set customer GSTIN for GST calculations
          const customerGstin = invoice.customerGSTIN || (invoice.customer?.GSTIN || '');
          setCustomerGSTIN(customerGstin || '');
          
          // Load projects for the customer when editing
          const customerId = invoice.customer?._id || invoice.customer;
          if (customerId) {
            loadCustomerProjects(customerId);
          }
          
          // Set service data for the service table
          // In edit/view/approve mode, only show invoice services (not all from master)
          if (invoice.serviceDetails && invoice.serviceDetails.length > 0) {
            const transformedServiceData = invoice.serviceDetails.map((service, index) => ({
              ...service.service, // Spread populated service master data
              _id: service.service?._id || service._id || service.service,
              serviceId: service.service?._id || service.service || service._id,
              serviceCode: service.serviceCode || service.service?.serviceCode || '',
              serviceDescription: service.serviceDescription || service.service?.serviceDescription || '',
              selectSAC: service.SACCode || service.service?.selectSAC || '',
              SACCode: service.SACCode || service.service?.selectSAC || '',
              gstRate: service.gstRate || service.service?.gstRate || 0,
              cgstRate: service.cgstRate || cgstRate,
              sgstRate: service.sgstRate || sgstRate,
              igstRate: service.igstRate || igstRate,
              quantity: service.qty || 0,
              qty: service.qty || 0,
              unitRate: service.unitRate || 0,
              rate: service.unitRate || 0,
              lineValue: service.lineValue || 0,
              additionalInfo: service.additionalInfo || '',
              serialNumber: index + 1 // Start from 1
            }));
            setServiceData(transformedServiceData);
            setShowServiceTable(true);
          } else {
            setServiceData([]);
            setShowServiceTable(false);
          }
        } else {
          toast.error('Failed to load invoice data');
        }
      }
    } catch (error) {
      console.error('Error loading invoice data:', error);
      toast.error('Failed to load invoice data');
    } finally {
      setLoading(false);
    }
  };

  const generateInvoiceNumber = async () => {
    setIsLoadingInvoiceNumber(true);
    try {
      // Get the current value for TAX-INVOICE sequence
      const response = await autoIncrementService.getCurrentValue('TAX-INVOICE');
      
      console.log('Auto-increment response:', response);
      
      // Handle different response structures
      let sequence = null;
      
      if (response.success && response.data) {
        sequence = response.data;
      } else if (response.data) {
        // Sometimes the data is directly in response.data
        sequence = response.data;
      } else if (response) {
        // Sometimes the response itself is the data
        sequence = response;
      }
      
      console.log('Sequence data:', sequence);
      
      if (sequence) {
        // Use the formattedValue directly from backend
        if (sequence.formattedValue) {
          console.log('Using backend formatted value:', sequence.formattedValue);
          setFormData(prev => ({
            ...prev,
            invoiceNumber: sequence.formattedValue
          }));
        } else {
          console.error('No formattedValue found in sequence:', sequence);
          // Set a default invoice number without showing error
          setFormData(prev => ({
            ...prev,
            invoiceNumber: 'TI-2025-001'
          }));
        }
      } else {
        console.error('No sequence data found in response:', response);
        // Set a default invoice number without showing error
        setFormData(prev => ({
          ...prev,
          invoiceNumber: 'TI-2025-001'
        }));
      }
    } catch (error) {
      console.error('Error generating invoice number:', error);
      
      // Check if it's an authentication error
      if (error.response?.status === 401) {
        toast.error('Please log in to continue');
        navigate('/login');
        return;
      }
      
      // Set a default invoice number without showing error
      setFormData(prev => ({
        ...prev,
        invoiceNumber: 'TI-2025-001'
      }));
    } finally {
      setIsLoadingInvoiceNumber(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const calculateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (item.taxableAmount || 0), 0);
    
    // Calculate GST based on state (simplified logic)
    const isInterState = formData.customerState !== 'NY'; // Assuming NY is our state
    const cgstAmount = isInterState ? 0 : (subtotal * formData.cgstRate) / 100;
    const sgstAmount = isInterState ? 0 : (subtotal * formData.sgstRate) / 100;
    const igstAmount = isInterState ? (subtotal * formData.igstRate) / 100 : 0;
    const totalTaxAmount = cgstAmount + sgstAmount + igstAmount;
    const totalAmount = subtotal + totalTaxAmount;

    setFormData(prev => ({
      ...prev,
      subtotal,
      cgstAmount,
      sgstAmount,
      igstAmount,
      totalTaxAmount,
      totalAmount
    }));
  };

  const hasBillableLines = formData.items.some((item) => {
    const qty = parseFloat(item.quantity || item.qty) || 0;
    const rate = parseFloat(item.unitRate || item.rate) || 0;
    return qty > 0 && rate > 0;
  });

  // An existing invoice can be saved from its current lines. A new invoice still
  // waits for Preview so only the lines with a quantity and rate are submitted.
  const canSaveInvoice = hasBillableLines && (isEdit || canCreateInvoice);

  const handleSaveClick = (e) => {
    if (!hasBillableLines) {
      toast.error('Enter a quantity and rate for at least one service before saving the invoice.');
      return;
    }
    if (!isEdit && !canCreateInvoice) {
      toast.info('Click Preview Selected after entering the quantity and rate, then save the invoice.');
      return;
    }
    handleSubmit(e);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get payment terms description from ID
      let paymentTermsDescription = '';
      if (formData.customerPaymentTerms) {
        const selectedTerm = paymentTermsOptions.find(
          term => (term._id || term.id) === formData.customerPaymentTerms
        );
        paymentTermsDescription = selectedTerm ? selectedTerm.description : '';
      }

      // Prepare data for submission
      const submissionData = {
        serviceInvoiceDate: formData.invoiceDate,
        dueDate: formData.dueDate,
        customerCategory: formData.customerCategory,
        customerName: formData.customerName,
        customer: formData.customerId,
        customerCode: formData.customerCode,
        customerGSTIN: formData.customerGSTIN,
        customerPaymentTerms: paymentTermsDescription || formData.customerPaymentTerms,
        PONo: formData.poNumber,
        PODate: formData.dueDate,
        projectName: formData.projectName || '',
        project: formData.projectId || null,
        referenceModel: formData.referenceModel || 'Customer',
        currency: formData.currency || 'INR', // Default to INR if not set (backend requirement)
        serviceDetails: (() => {
          const liveLines = serviceTableRef.current?.getBillableLines?.() || [];
          return liveLines.length > 0 ? liveLines : formData.items;
        })().map(item => ({
          service: item.serviceId || item._id || item.id, // Service ObjectId reference (required)
          serviceCode: item.serviceCode || item.itemCode || '',
          serviceDescription: item.serviceDescription || item.itemName || item.description || '',
          SACCode: item.SACCode || item.selectSAC || '', // Map from selectSAC if SACCode not available (required)
          gstRate: item.gstRate || item.igstRate || ((Number(item.cgstRate) || 0) + (Number(item.sgstRate) || 0)) || 0,
          igstRate: item.igstRate || formData.igstRate || 18,
          sgstRate: item.sgstRate || formData.sgstRate || 9,
          cgstRate: item.cgstRate || formData.cgstRate || 9,
          additionalInfo: item.additionalInfo || '',
          qty: item.quantity || item.qty || 0,
          unitRate: item.unitRate || item.rate || 0,
          lineValue: item.lineValue || (item.quantity * item.rate) || 0
        })).filter(item => item.service && item.SACCode && item.qty > 0 && item.unitRate > 0), // Filter out invalid items
        paymentTerms: paymentTermsDescription || formData.customerPaymentTerms,
        remarks: formData.remark
      };

      if (submissionData.serviceDetails.length === 0) {
        toast.error('Enter a quantity and rate for at least one service before saving the invoice.');
        return;
      }

      let response;
      if (isEdit) {
        response = await taxInvoiceService.updateTaxInvoice(id, submissionData);
      } else {
        // Always set status to 'Draft' when creating
        submissionData.status = 'Draft';
        response = await taxInvoiceService.createTaxInvoice(submissionData);
      }

      if (response.success) {
        toast.success(isEdit ? 'Tax invoice updated successfully' : 'Tax invoice created successfully');
        navigate('/sales/tax-invoice');
      } else {
        toast.error(response.message || 'Failed to save tax invoice');
      }
    } catch (error) {
      console.error('Error saving tax invoice:', error);
      toast.error(error.response?.data?.message || 'Error saving tax invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/sales/tax-invoice');
  };

  const handleApprove = () => {
    if (!id || id === 'new') {
      toast.error('Please save the invoice first before approving');
      return;
    }

    if (formData.status !== 'Draft') {
      toast.warning(`Invoice status is already ${formData.status}. Only Draft invoices can be approved.`);
      return;
    }

    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    setShowApproveModal(false);
    setLoading(true);
    try {
      const response = await taxInvoiceService.updateTaxInvoice(id, {
        status: 'Approved'
      });

      if (response.success) {
        toast.success('Tax invoice approved successfully');
        // Navigate back to list page (approved invoices won't show there)
        navigate('/sales/tax-invoice');
      } else {
        toast.error(response.message || 'Failed to approve tax invoice');
      }
    } catch (error) {
      console.error('Error approving tax invoice:', error);
      toast.error('Failed to approve tax invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerSearch = () => {
    setShowCustomerModal(true);
  };

  const handleCustomerSelect = (selectedCustomer) => {
    // Find matching payment terms from options
    let defaultPaymentTerms = '';
    if (selectedCustomer.customerPaymentTerms && paymentTermsOptions.length > 0) {
      const customerPaymentTermsStr = selectedCustomer.customerPaymentTerms.toString().trim();
      
      // Try to match by description first (exact match)
      let matchedTerm = paymentTermsOptions.find(
        term => term.description && term.description.toString().trim() === customerPaymentTermsStr
      );
      
      // If no exact match, try partial match on description
      if (!matchedTerm) {
        matchedTerm = paymentTermsOptions.find(
          term => term.description && term.description.toString().trim().includes(customerPaymentTermsStr)
        );
      }
      
      // If still no match, try to match by paymentTermsCode
      if (!matchedTerm) {
        matchedTerm = paymentTermsOptions.find(
          term => term.paymentTermsCode && term.paymentTermsCode.toString().trim() === customerPaymentTermsStr
        );
      }
      
      if (matchedTerm) {
        defaultPaymentTerms = matchedTerm._id || matchedTerm.id;
      }
    }

    // Determine customer category - check both categoryType and customerCategory fields
    // TaxInvoice requires: 'Domestic', 'Export', 'SEZ', 'Deemed Export'
    // Customer model has: categoryType (enum: 'Domestic', 'Export') and customerCategory (string)
    let customerCategory = 'Domestic'; // Default value
    
    if (selectedCustomer.categoryType) {
      // categoryType is enum: 'Domestic' or 'Export'
      customerCategory = selectedCustomer.categoryType;
    } else if (selectedCustomer.customerCategory) {
      // customerCategory is a string field - map it to valid TaxInvoice values
      const cat = selectedCustomer.customerCategory.toString().trim();
      if (['Domestic', 'Export', 'SEZ', 'Deemed Export'].includes(cat)) {
        customerCategory = cat;
      } else if (cat.toLowerCase() === 'export' || cat.toLowerCase() === 'international') {
        customerCategory = 'Export';
      } else {
        customerCategory = 'Domestic'; // Default for any other value
      }
    }

    const billingAddr = selectedCustomer.customerBillingAddress?.[0] || {};

    setFormData(prev => ({
      ...prev,
      customerId: selectedCustomer._id || selectedCustomer.id,
      customerName: selectedCustomer.customerName,
      customerCode: selectedCustomer.customerCode, // Keep for backend submission
      customerGSTIN: selectedCustomer.GSTIN || '',
      customerState: billingAddr.state || '',
      billingState: billingAddr.state || '',
      placeOfSupply: billingAddr.state || '',
      customerPaymentTerms: defaultPaymentTerms,
      customerCategory: customerCategory, // Set customer category from customer data
      currency: selectedCustomer.customerCurrency || '', // Populate currency from customer
      projectId: '', // Reset project when customer changes
      projectName: '' // Reset project name when customer changes
    }));
    setCustomerGSTIN(selectedCustomer.GSTIN || '');
    setShowCustomerModal(false);
    setShowServiceTable(true); // Show service table when customer is selected
    
    // Load projects for the selected customer
    loadCustomerProjects(selectedCustomer._id || selectedCustomer.id);
  };

  // Load projects for selected customer
  const loadCustomerProjects = async (customerId) => {
    if (!customerId) {
      setProjectOptions([]);
      return;
    }

    try {
      setLoadingProjects(true);
      const response = await projectService.getAllProjects({
        customer: customerId,
        status: 'Active',
        limit: 1000, // Get all active projects for the customer
        page: 1
      });

      if (response && response.success) {
        // Backend returns projects array directly or in response.projects
        const projectsList = response.projects || response.data || [];
        const projects = (Array.isArray(projectsList) ? projectsList : []).map(project => ({
          _id: project._id,
          id: project._id,
          projectName: project.projectName,
          projectNumber: project.projectNumber,
          projectDescription: project.projectDescription || ''
        }));
        setProjectOptions(projects);
      } else {
        setProjectOptions([]);
      }
    } catch (error) {
      console.error('Error loading customer projects:', error);
      setProjectOptions([]);
      // Don't show error toast - just silently fail
    } finally {
      setLoadingProjects(false);
    }
  };

  // Handle project selection
  const handleProjectSelect = (projectId) => {
    const selectedProject = projectOptions.find(p => (p._id || p.id) === projectId);
    setFormData(prev => ({
      ...prev,
      projectId: projectId || '',
      projectName: selectedProject ? selectedProject.projectName : ''
    }));
  };

  const handleServiceDataChange = (updatedServiceData) => {
    setServiceData(updatedServiceData);
    
    // Calculate total value from all services and transform to items format
    const totalValue = updatedServiceData.reduce((sum, service) => {
      const qty = parseFloat(service.quantity) || 0;
      const rate = parseFloat(service.unitRate) || 0;
      const lineValue = parseFloat(service.lineValue) || (qty * rate);
      return sum + lineValue;
    }, 0);
    
    // Transform service data to items format with all required fields
    const transformedItems = updatedServiceData
      .filter(service => {
        const qty = parseFloat(service.quantity) || 0;
        const rate = parseFloat(service.unitRate) || 0;
        return qty > 0 && rate > 0;
      })
      .map(service => ({
        id: service._id || service.id || service.serviceId,
        serviceId: service._id || service.id || service.serviceId, // Service ObjectId
        itemCode: service.serviceCode || '',
        itemName: service.serviceDescription || '',
        serviceDescription: service.serviceDescription || '',
        description: service.serviceDescription || '',
        SACCode: service.selectSAC || service.SACCode || '', // Map selectSAC to SACCode
        selectSAC: service.selectSAC || service.SACCode || '',
        gstRate: service.gstRate || 0,
        igstRate: service.igstRate || formData.igstRate || 18,
        sgstRate: service.sgstRate || formData.sgstRate || 9,
        cgstRate: service.cgstRate || formData.cgstRate || 9,
        quantity: parseFloat(service.quantity) || 0,
        qty: parseFloat(service.quantity) || 0,
        unit: 'Nos',
        rate: parseFloat(service.unitRate) || 0,
        unitRate: parseFloat(service.unitRate) || 0,
        amount: parseFloat(service.lineValue) || (parseFloat(service.quantity) * parseFloat(service.unitRate)) || 0,
        lineValue: parseFloat(service.lineValue) || (parseFloat(service.quantity) * parseFloat(service.unitRate)) || 0,
        taxableAmount: parseFloat(service.lineValue) || (parseFloat(service.quantity) * parseFloat(service.unitRate)) || 0,
        discount: 0,
        additionalInfo: service.additionalInfo || ''
      }));
    
    // Update form data with service information
    setFormData(prev => ({
      ...prev,
      items: transformedItems,
      totalValue: Math.round(totalValue * 100) / 100 // Round to 2 decimal places
    }));

    // Calculate GST after updating total value
    calculateGST(totalValue);
    
    // Update canCreateInvoice state after service data changes
    if (serviceTableRef.current) {
      setCanCreateInvoice(serviceTableRef.current.canCreateInvoice || false);
    }
  };

  // GST Calculation Logic — uses GSTIN or place of supply for unregistered customers
  const calculateGST = (subtotal) => {
    const gstRate = formData.igstRate || 18;
    const result = calculateInvoiceGST({
      subtotal,
      supplierGSTIN: companyGSTIN,
      customerGSTIN: customerGSTIN || formData.customerGSTIN,
      customerCategory: formData.customerCategory || 'Domestic',
      placeOfSupply: formData.placeOfSupply || formData.billingState || formData.customerState,
      supplierState: companyState,
      gstRate,
      cgstRate: formData.cgstRate || 9,
      sgstRate: formData.sgstRate || 9,
      igstRate: formData.igstRate || 18,
    });

    setFormData(prev => ({
      ...prev,
      subtotal: result.subtotal,
      cgstAmount: result.cgstAmount,
      sgstAmount: result.sgstAmount,
      igstAmount: result.igstAmount,
      totalTaxAmount: result.totalTaxAmount,
      totalAmountBeforeRoundOff: result.totalAmountBeforeRoundOff,
      roundOffAmount: result.roundOffAmount,
      totalAmount: result.totalAmount,
      gstType: result.gstType,
    }));
  };

  // Handle GST rate changes
  const handleGSTRateChange = (field, value) => {
    const numericValue = parseFloat(value) || 0;
    setFormData(prev => ({
      ...prev,
      [field]: numericValue
    }));
    
    // Recalculate GST with new rates
    calculateGST(formData.totalValue || 0);
  };

  // Reset service table when customer is cleared
  const handleCustomerClear = () => {
    setFormData(prev => ({
      ...prev,
      customerId: '',
      customerName: '',
      customerCode: '',
      customerGSTIN: '',
      customerState: '',
      billingState: '',
      placeOfSupply: '',
      customerPaymentTerms: '',
      currency: ''
    }));
    setCustomerGSTIN('');
    setShowServiceTable(false);
    setServiceData([]);
    
    // Reset GST calculations
    calculateGST(0);
  };

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Transactions',
      path: '/sales?tab=transactions',
      onClick: () => navigate('/sales?tab=transactions')
    },
    {
      label: 'Tax Invoice',
      path: '/sales/tax-invoice',
      onClick: () => navigate('/sales/tax-invoice')
    },
    {
      label: isEdit ? 'Edit Invoice' : isView ? 'View Invoice' : 'Create Invoice',
      path: window.location.pathname,
      isCurrent: true
    }
  ];

  const helpContent = (
    <>
      <h3>What is Tax Invoice?</h3>
      <p>Tax Invoice is a <strong>tax-compliant sales invoice</strong> for billing customers. It includes invoice number, dates, customer and billing details, line items with GST (CGST/SGST/IGST), subtotal, tax, and total.</p>
      <h3>Tabs</h3>
      <p><strong>Invoice Details</strong> – Invoice number, date, due date, PO/reference, customer (select from master), billing address, and line items (add services via table).</p>
      <p><strong>GST &amp; Totals</strong> – Tax rates and amounts, subtotal, total. Optional project reference.</p>
      <p><strong>Notes &amp; Terms</strong> – Notes, terms and conditions, status, remark.</p>
      <h3>How to use</h3>
      <p>Select a <strong>customer</strong> first, then add line items (use service selection if available). Fill dates and optional PO/reference. Save as Draft or submit. In edit mode you can update; in approve mode you can approve the invoice.</p>
    </>
  );

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb
          items={breadcrumbItems}
          rightSlot={
            <button type="button" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1" onClick={() => setShowHelp(true)} title="Help" aria-label="Open help">
              <HelpCircle size={18} />
              <span className="d-none d-sm-inline">Help</span>
            </button>
          }
        />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>{isApprove ? 'Approve Tax Invoice' : isEdit ? 'Edit Tax Invoice' : isView ? 'View Tax Invoice' : 'Create Tax Invoice'}</h1>
          <p>{isApprove ? 'Review and approve tax invoice' : isEdit ? 'Update tax invoice details' : isView ? 'View tax invoice information' : 'Generate new tax invoice'}</p>
        </div>
        {/* Show page actions in create mode or approve mode */}
        {(!isEdit || isApprove) && (
          <div className="page-actions" style={{ display: 'flex', gap: '12px' }}>
            {!isView && !isApprove && (
              <button 
                type="submit" 
                form="tax-invoice-form"
                className="btn-primary" 
                disabled={loading}
              >
                <Save size={18} />
                {loading ? 'Saving...' : (isEdit ? 'Update Invoice' : 'Create Invoice')}
              </button>
            )}
            {(isApprove || (!isView && isEdit && formData.status === 'Draft')) && (
              <button 
                type="button"
                className="btn-primary" 
                onClick={handleApprove}
                disabled={loading || (isApprove && formData.status !== 'Draft')}
                style={{ backgroundColor: '#10b981', borderColor: '#10b981' }}
              >
                <CheckCircle size={18} />
                {loading ? 'Approving...' : 'Approve Invoice'}
              </button>
            )}
            <button className="btn-secondary" onClick={handleCancel}>
              <X size={18} />
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Form Section */}
      <div className="form-container pay-entry">
        <div className="sac-form-header">
          <h2>Tax Invoice Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'invoiceDetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('invoiceDetails')}
          >
            <FileText size={18} />
            <span>Invoice Details</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'gstDetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('gstDetails')}
          >
            <Calculator size={18} />
            <span>GST Details</span>
          </button>
        </div>
          
        <form id="tax-invoice-form" className="form-content" onSubmit={handleSubmit}>
          
          {activeTab === 'invoiceDetails' && (
            <div className="tab-content">
              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Invoice</h3>
                    <p>The number, date, customer, and whether this is a domestic or export invoice.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-4">
                <div className="form-group">
                  <label htmlFor="invoiceNumber" className="form-label required">
                    Invoice #
                  </label>
                  <input
                    type="text"
                    id="invoiceNumber"
                    name="invoiceNumber"
                    value={isLoadingInvoiceNumber ? 'Generating...' : formData.invoiceNumber}
                    readOnly
                    className="form-input pay-readonly"
                    placeholder="Auto-generated"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="invoiceDate" className="form-label required">
                    Invoice Date
                  </label>
                  <input
                    type="date"
                    id="invoiceDate"
                    name="invoiceDate"
                    value={formData.invoiceDate}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isView || isApprove}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerCategory" className="form-label required">
                    Customer/Sales Category
                  </label>
                  <select
                    id="customerCategory"
                    name="customerCategory"
                    value={formData.customerCategory}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isView || isApprove}
                  >
                    <option value="">Select</option>
                    <option value="Domestic">Domestic</option>
                    <option value="Export">Export</option>
                    <option value="SEZ">SEZ</option>
                    <option value="Deemed Export">Deemed Export</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="customerName" className="form-label required">
                    Customer Name
                  </label>
                  <div className="search-input-wrapper">
                    <input
                      type="text"
                      id="customerName"
                      name="customerName"
                      value={formData.customerName}
                      readOnly
                      className="form-input search-input"
                      placeholder="Select customer"
                      style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                      disabled={isView || isApprove}
                    />
                    <button
                      type="button"
                      className="search-button"
                      title="Search Customer"
                      onClick={handleCustomerSearch}
                      disabled={isView || isApprove}
                    >
                      <Search size={16} />
                    </button>
                  </div>
                </div>
              </div>
              </section>

              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Order</h3>
                    <p>The project, purchase order, and payment terms this invoice follows.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-4">
                <div className="form-group">
                  <label htmlFor="projectId" className="form-label required">
                    Project Name / CR Name
                  </label>
                  <select
                    id="projectId"
                    name="projectId"
                    value={formData.projectId}
                    onChange={(e) => handleProjectSelect(e.target.value)}
                    className="form-input"
                    disabled={isView || isApprove || !formData.customerId || loadingProjects}
                  >
                    <option value="">Select</option>
                    {loadingProjects ? (
                      <option value="" disabled>Loading projects...</option>
                    ) : projectOptions.length === 0 && formData.customerId ? (
                      <option value="" disabled>No active projects found</option>
                    ) : (
                      projectOptions.map((project) => (
                        <option key={project._id || project.id} value={project._id || project.id}>
                          {project.projectName} {project.projectNumber ? `(${project.projectNumber})` : ''}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="dueDate" className="form-label required">
                    Purchase Order Date
                  </label>
                  <input
                    type="date"
                    id="dueDate"
                    name="dueDate"
                    value={formData.dueDate}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isView || isApprove}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="poNumber" className="form-label required">
                    Purchase Order No.
                  </label>
                  <input
                    type="text"
                    id="poNumber"
                    name="poNumber"
                    value={formData.poNumber}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter PO number"
                    disabled={isView || isApprove}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerPaymentTerms" className="form-label required">
                    Payment Terms
                  </label>
                  <select
                    id="customerPaymentTerms"
                    name="customerPaymentTerms"
                    value={formData.customerPaymentTerms}
                    onChange={handleInputChange}
                    className="form-input"
                    disabled={isView || isApprove}
                  >
                    <option value="">Select Payment Terms</option>
                    {paymentTermsOptions.map((term) => (
                      <option key={term._id || term.id} value={term._id || term.id}>
                        {term.paymentTermsCode} - {term.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              </section>

              {showServiceTable && (
                <section className="pay-block">
                  <div className="pay-block-head">
                    <div>
                      <h3>Services</h3>
                      <p>Select the services and enter the quantity and rate for this invoice.</p>
                    </div>
                  </div>
                  <ServiceSelectionTable 
                      ref={serviceTableRef}
                      onServiceDataChange={(updatedServiceData) => {
                        handleServiceDataChange(updatedServiceData)
                        // Check canCreateInvoice state after data change
                        setTimeout(() => {
                          if (serviceTableRef.current) {
                            setCanCreateInvoice(serviceTableRef.current.canCreateInvoice || false)
                          }
                        }, 0)
                      }}
                      initialServiceData={serviceData}
                      isEditMode={isEdit}
                      isViewMode={isView}
                      isApproveMode={isApprove}
                    />
                </section>
              )}

            </div>
          )}

          {activeTab === 'gstDetails' && (
            <div className="tab-content">
              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Customer tax</h3>
                    <p>Taken from the customer. The invoice currency stays as it is on the customer.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-2">
                <div className="form-group">
                  <label htmlFor="customerGSTIN" className="form-label">
                    GSTIN
                  </label>
                  <input
                    type="text"
                    id="customerGSTIN"
                    name="customerGSTIN"
                    value={formData.customerGSTIN}
                    readOnly
                    className="form-input pay-readonly"
                    placeholder="Select a customer"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="currency" className="form-label required">
                    Currency
                  </label>
                  <input
                    type="text"
                    id="currency"
                    name="currency"
                    value={formData.currency || ''}
                    readOnly
                    className="form-input pay-readonly"
                    placeholder="Select a customer"
                  />
                </div>
              </div>
              </section>

              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Tax rates</h3>
                    <p>Calculated from the place of supply. Export invoices under LUT stay at zero.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-3">
                <div className="form-group">
                  <label htmlFor="cgstRate" className="form-label">
                    CGST Rate (%)
                  </label>
                  <input
                    type="number"
                    id="cgstRate"
                    name="cgstRate"
                    value={formData.cgstRate}
                    readOnly
                    className="form-input pay-readonly"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="sgstRate" className="form-label">
                    SGST Rate (%)
                  </label>
                  <input
                    type="number"
                    id="sgstRate"
                    name="sgstRate"
                    value={formData.sgstRate}
                    readOnly
                    className="form-input pay-readonly"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="igstRate" className="form-label">
                    IGST Rate (%)
                  </label>
                  <input
                    type="number"
                    id="igstRate"
                    name="igstRate"
                    value={formData.igstRate}
                    readOnly
                    className="form-input pay-readonly"
                  />
                </div>
              </div>
              </section>

              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Tax summary</h3>
                    <p>Amounts stay in {formData.currency || 'INR'}.</p>
                  </div>
                </div>
                <div className="pay-facts">
                  <div className="pay-fact">
                    <span>Subtotal</span>
                    <strong>{formatCurrency(formData.subtotal || 0, formData.currency || 'INR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </div>
                  <div className="pay-fact">
                    <span>CGST</span>
                    <strong>{formatCurrency(formData.cgstAmount || 0, formData.currency || 'INR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </div>
                  <div className="pay-fact">
                    <span>SGST</span>
                    <strong>{formatCurrency(formData.sgstAmount || 0, formData.currency || 'INR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </div>
                  <div className="pay-fact">
                    <span>IGST</span>
                    <strong>{formatCurrency(formData.igstAmount || 0, formData.currency || 'INR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </div>
                  <div className="pay-fact">
                    <span>Total tax</span>
                    <strong>{formatCurrency(formData.totalTaxAmount || 0, formData.currency || 'INR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                  </div>
                </div>
                <div className="pay-convert-row" style={{ marginTop: 14 }}>
                  {Math.abs(formData.roundOffAmount || 0) >= 0.001 && (
                    <div className="pay-fact">
                      <span>Round off</span>
                      <strong>{formatCurrency(formData.roundOffAmount || 0, formData.currency || 'INR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                    </div>
                  )}
                  <div className="pay-inr">
                    <span>Amount payable</span>
                    <strong>{formatCurrency(Math.round(formData.totalAmount || 0), formData.currency || 'INR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</strong>
                    <p>The amount due on this invoice.</p>
                  </div>
                </div>
              </section>

              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Notes</h3>
                    <p>Anything that should print with this invoice.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-2">
                <div className="form-group">
                  <label htmlFor="notes" className="form-label">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Additional notes or comments"
                    rows={3}
                    disabled={isView || isApprove}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="terms" className="form-label">
                    Terms & Conditions
                  </label>
                  <textarea
                    id="terms"
                    name="terms"
                    value={formData.terms}
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Payment terms and conditions"
                    rows={3}
                    disabled={isView || isApprove}
                  />
                </div>
              </div>
              </section>
            </div>
          )}

          {/* Form Actions */}
        </form>

        <div className="pay-footer">
          <div className="pay-footer-fields">
            <div className="form-group">
              <label className="form-label">Total value</label>
              <input
                type="text"
                value={formatCurrency(formData.totalValue || 0, formData.currency || 'INR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                readOnly
                className="form-input pay-readonly pay-total"
              />
            </div>
            <div className="form-group">
              <label htmlFor="remark" className="form-label">Remark</label>
              <input
                type="text"
                id="remark"
                name="remark"
                value={formData.remark || ''}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter remarks..."
                disabled={isView || isApprove}
              />
            </div>
          </div>

          {!isApprove && (
            <div className="pay-footer-actions">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => {
                  window.location.reload();
                }}
                disabled={isView}
              >
                Reset
              </button>
              
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => {
                  // Call Esc handler from ServiceSelectionTable
                  if (serviceTableRef.current && serviceTableRef.current.handleEsc) {
                    serviceTableRef.current.handleEsc()
                    // Reset canCreateInvoice state after Esc (preview mode is reset)
                    setTimeout(() => {
                      setCanCreateInvoice(false)
                    }, 100)
                  }
                }}
                disabled={isView}
                title={isEdit ? "Load all available services from service master while keeping current invoice services. Use this to add more services to your invoice." : "Load all available services from service master while keeping services you've already filled. Use this to add more services to your invoice."}
              >
                Add More Services
              </button>
              
              <button 
                type="button" 
                className="btn-secondary"
                onClick={() => {
                  // Call Preview handler from ServiceSelectionTable
                  if (serviceTableRef.current && serviceTableRef.current.handlePreview) {
                    serviceTableRef.current.handlePreview()
                    // Update canCreateInvoice state after preview
                    setTimeout(() => {
                      if (serviceTableRef.current) {
                        setCanCreateInvoice(serviceTableRef.current.canCreateInvoice || false)
                      }
                    }, 100)
                  }
                }}
                disabled={isView}
                title="Filter the table to show only services with quantity and rate entered. This helps you review the services that will be included in your invoice before creating it."
              >
                Preview Selected
              </button>
              
              {!isView && (
                <button 
                  type="button" 
                  className="btn-primary" 
                  onClick={handleSaveClick}
                  disabled={loading}
                  title={canSaveInvoice ? '' : (isEdit ? 'Enter a quantity and rate for at least one service' : 'Click Preview Selected after entering quantity and rate')}
                >
                  {loading ? 'Saving...' : (isEdit ? 'Update Invoice' : 'Create Invoice')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Customer Selection Modal */}
      <CustomerSelectionModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onSelectCustomer={handleCustomerSelect}
      />

      {/* Approve Confirmation Modal */}
      <ConfirmationModal
        isOpen={showApproveModal}
        onClose={() => setShowApproveModal(false)}
        onConfirm={confirmApprove}
        title="Approve Tax Invoice"
        message={`Are you sure you want to approve invoice ${formData.invoiceNumber}? This action cannot be undone.`}
        confirmText="Approve Invoice"
        cancelText="Cancel"
        type="success"
      />

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title="Tax Invoice – Help">
        {helpContent}
      </HelpModal>
    </div>
  );
};

export default TaxInvoiceForm;
